/**
 * Domain Entity: Practice
 * Represents a swimming practice session with business logic
 */
export class Practice {
  private constructor(
    private readonly _id: string,
    private readonly _practiceId: string,
    private readonly _coachId: number,
    private readonly _swimmerId: number,
    private readonly _name: string,
    private readonly _description: string,
    private readonly _date: Date,
    private readonly _practice_time: string,
    private readonly _courseTypeLookupId: number,
    private readonly _isFavorite: boolean,
    private readonly _isFinished: boolean,
    private readonly _imageData: string | null,
    private readonly _folderId: string | null,
    private readonly _folderPracticeId: string | null,
    private readonly _folderPracticeModelId: string | null,
    private readonly _createdAt: Date = new Date(),
    private readonly _updatedAt: Date = new Date()
  ) {}

  /**
   * Create a new Practice domain entity (normal or folder)
   */
  static create(params: {
  id: string;
  practiceId: string;
  coachId?: number | null;
  swimmerId: number;
  name: string;
  description: string;
  date: Date;
  practice_time: string;
  courseTypeLookupId: number;
  folderId?: string | null;
  folder_practice_id?: string | null;
  folder_practice_model_id?: string | null;
  isFavorite?: boolean;
  isFinished?: boolean;
  imageData?: string | null;
  createdAt?: Date;
  updatedAt?: Date;
}): Practice {
  const {
    id,
    practiceId,
    coachId = null,
    swimmerId,
    name,
    description,
    date,
    practice_time,
    courseTypeLookupId,
    folderId = null,
    folder_practice_id = null,
    folder_practice_model_id = null,
    isFavorite = false,
    isFinished = false,
    imageData = null,
    createdAt = new Date(),
    updatedAt = new Date()
  } = params;

  // Required fields based on backend
  if (!id || !practiceId || !swimmerId || !name) {
    throw new Error("Practice id, practiceId, swimmerId, and name are required");
  }

  if (!practice_time) {
    throw new Error("practice_time cannot be null or empty");
  }

  return new Practice(
    id,
    practiceId,
    coachId ?? 0,
    swimmerId,
    name,
    description,
    date,
    practice_time,
    courseTypeLookupId,
    isFavorite,
    isFinished,
    imageData,
    folderId,
    folder_practice_id,
    folder_practice_model_id,
    createdAt,
    updatedAt
  );
}


  /**
   * Hydrate from raw API data
   */
  static fromData(data: any): Practice {
    const isFolderPractice = !!data.folder_practice_id;

    if (isFolderPractice) {
      const model = data.practice_model;

      return new Practice(
        data.folder_practice_id,
        model.practice_id,
        Number(model.coach_id),
        Number(model.user_id),
        model.name,
        model.description,
        new Date(model.practice_date),
        model.practice_time,
        Number(model.CourseTypeLookupId),
        model.isFavorite === 1 || model.isFavorite === true,
        model.isFinished === "1" || model.isFinished === true,
        model.imageData ?? null,
        data.folder_id,
        data.folder_practice_id,
        model.folder_practice_model_id ?? data.folder_practice_model_id,
        model.createdAt ? new Date(model.createdAt) : new Date(),
        model.updated_at ? new Date(model.updated_at) : new Date()
      );
    }

    return new Practice(
      data.id,
      data.practice_id,
      data.coach_id,
      data.swimmer_id,
      data.name,
      data.description,
      new Date(data.practice_date),
      data.practice_time,
      data.practice_CourseTypeLookupId,
      data.isFavorite === 1 || data.isFavorite === true,
      data.isFinished === "1" || data.isFinished === true,
      data.imageData ?? null,
      data.folder_id ?? null,
      data.folder_practice_id ?? null,
      data.folder_practice_model_id ?? null,
      data.createdAt ? new Date(data.createdAt) : new Date(),
      data.updatedAt ? new Date(data.updatedAt) : new Date()
    );
  }

  /** Getters */
  get id(): string {
    return this._id;
  }

  get practiceId(): string {
    return this._practiceId;
  }

  get coachId(): number {
    return this._coachId;
  }

  get swimmerId(): number {
    return this._swimmerId;
  }

  get name(): string {
    return this._name;
  }

  get description(): string {
    return this._description;
  }

  get date(): Date {
    return this._date;
  }

  get practice_time(): string {
    return this._practice_time;
  }

  get courseTypeLookupId(): number {
    return this._courseTypeLookupId;
  }

  get isFavorite(): boolean {
    return this._isFavorite;
  }

  get isFinished(): boolean {
    return this._isFinished;
  }

  get imageData(): string | null {
    return this._imageData;
  }

  get folderId(): string | null {
    return this._folderId;
  }

  get folderPracticeId(): string | null {
    return this._folderPracticeId;
  }

  get folderPracticeModelId(): string | null {
    return this._folderPracticeModelId;
  }

  get createdAt(): Date {
    return this._createdAt;
  }

  get updatedAt(): Date {
    return this._updatedAt;
  }

  /** Derived values */
  isToday(): boolean {
    const today = new Date();
    return this._date.toDateString() === today.toDateString();
  }

  isThisWeek(): boolean {
    const now = new Date();
    const weekStart = new Date(now.setDate(now.getDate() - now.getDay()));
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    return this._date >= weekStart && this._date <= weekEnd;
  }

  /** JSON output */
  toJSON() {
    return {
      id: this._id,
      practice_id: this._practiceId,
      coach_id: this._coachId,
      swimmer_id: this._swimmerId, 
      name: this._name,
      description: this._description,
      practice_date: this._date.toISOString().split("T")[0],
      practice_time: this._practice_time,
      practice_CourseTypeLookupId: this._courseTypeLookupId,
      folder_id: this._folderId,
      folder_practice_id: this._folderPracticeId,
      folder_practice_model_id: this._folderPracticeModelId,
      isFavorite: this._isFavorite ? 1 : 0,
      isFinished: this._isFinished ? "1" : "0",
      imageData: this._imageData,
      createdAt: this._createdAt.toISOString(),
      updatedAt: this._updatedAt.toISOString()
    };
  }
}
